import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Typography,
} from "@mui/material";

import { useTranslation } from "react-i18next";
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  HistoryToggleOff as HistoryToggleOffIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";

import * as Styled from "./styles";
import TimePicker from "components/TimePcker";
import { useAppDispatch, useAppSelector } from "store/hooks";
import HolidayWarning from "./HolidayWarning/HolidayWarning.component";
import { handleGetWorkingAreaRequest } from "store/modules/Logistics/thunk.store";
import { getHolidayPayload } from "./working-hour.helpers";
import {
  WorkingHourComponentProps,
  WorkingHourFormData,
} from "./working-hour.types";
import { WeekDayKey } from "pages/ProductsAndCoverage/components/MassEditWorkingHoursMenu/types";
import { getCoverageAreaDealerId } from "store/modules/Logistics/logisticsCoverageArea.helpers";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";

const WorkingHour = ({
  workingHourData,
  onUpdate,
  dealerSelected,
}: WorkingHourComponentProps) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const currentCoverageArea = useAppSelector(
    (state) => state.logistics.workingAreaDetails[dealerSelected],
  );

  const DAYS_OF_WEEK_MAPPING: Record<WeekDayKey, string> = useMemo(
    () => ({
      "0": "Sunday",
      "1": "Monday",
      "2": "Tuesday",
      "3": "Wednesday",
      "4": "Thursday",
      "5": "Friday",
      "6": "Saturday",
    }),
    [],
  );

  const holidayOpeningHours = useMemo(
    () => currentCoverageArea?.holidayOpeningHours || [],
    [currentCoverageArea?.holidayOpeningHours],
  );

  const getHolidayForm = useCallback(() => {
    return holidayOpeningHours.length
      ? [holidayOpeningHours[0]]
      : [{ day: "Holiday", open: null, close: null, enabled: false }];
  }, [holidayOpeningHours]);

  const holidayForm = useMemo(() => getHolidayForm(), [getHolidayForm]);

  const getHoliday = useCallback(
    (day: string) => {
      const holidays = currentCoverageArea?.holidays;
      const holiday = holidays.find(
        (item: CoverageAreaProps["holidays"][number]) =>
          DAYS_OF_WEEK_MAPPING[item.dayOfWeek as WeekDayKey] === day,
      );

      return holiday ?? null;
    },
    [DAYS_OF_WEEK_MAPPING, currentCoverageArea?.holidays],
  );

  const defaultValues: any = useMemo(() => {
    const defaultValuesObj = {};

    const workingHourDataWithHoliday: any = workingHourData
      ? [...workingHourData, holidayForm[0] ?? {}]
      : [];

    workingHourDataWithHoliday?.forEach((item: any) => {
      Object.assign(defaultValuesObj, {
        [`enabled-${item?.day}`]: item?.enabled,
        [`open-${item?.day}`]: item?.open,
        [`close-${item?.day}`]: item?.close,
      });
    });

    return defaultValuesObj;
  }, [holidayForm, workingHourData]);

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<WorkingHourFormData>({
    defaultValues,
  });

  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const holidayWatcher = watch("enabled-Holiday");
  const switchHolidayWatcher = !!watch("holiday-switch");

  const [expand, setExpand] = React.useState(false);

  const handleChangeEditMode = useCallback(() => {
    setEditMode((prevS) => !prevS);
  }, []);

  const validateTime = (time: string) => {
    if (!time) return false;
    const [hours, minutes] = time.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  const validateTimeRange = (openTime: string, closeTime: string) => {
    if (!openTime || !closeTime) return true;
    const [openHours, openMinutes] = openTime.split(":").map(Number);
    const [closeHours, closeMinutes] = closeTime.split(":").map(Number);

    const openTotalMinutes = openHours * 60 + openMinutes;
    const closeTotalMinutes = closeHours * 60 + closeMinutes;

    return closeTotalMinutes > openTotalMinutes;
  };

  const timeValidationRule = useMemo(
    () => ({
      validate: (value: string) => {
        if (!value) return true;
        return validateTime(value) || t("invalidTime");
      },
    }),
    [t],
  );

  const timeRangeValidationRule = useMemo(
    () => (day: string) => ({
      validate: () => {
        const openTime = watch(`open-${day}`) as string;
        const closeTime = watch(`close-${day}`) as string;
        return (
          validateTimeRange(openTime, closeTime) || t("closeTimeMustBeGreater")
        );
      },
    }),
    [t, watch],
  );

  const submitWorkingHourUpdate = useCallback(
    async (formData: WorkingHourFormData) => {
      setIsLoading(true);
      try {
        const updatedWorkingHourData =
          workingHourData?.map((wkHourData) => ({
            ...wkHourData,
            open: formData[`open-${wkHourData.day}`] as string,
            close: formData[`close-${wkHourData.day}`] as string,
            enabled: formData[`enabled-${wkHourData.day}`] as boolean,
          })) || [];

        const holidayOpeningHoursPayload = getHolidayPayload(
          !!switchHolidayWatcher,
          formData,
          !!holidayWatcher,
        );

        await onUpdate(updatedWorkingHourData, holidayOpeningHoursPayload);

        await dispatch(
          handleGetWorkingAreaRequest({
            dealerId: getCoverageAreaDealerId(currentCoverageArea),
            coverageAreaId: dealerSelected,
          }),
        ).unwrap();

        handleChangeEditMode();
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentCoverageArea,
      dealerSelected,
      dispatch,
      handleChangeEditMode,
      holidayWatcher,
      onUpdate,
      switchHolidayWatcher,
      workingHourData,
    ],
  );

  const handleSetFormValues = useCallback(() => {
    if (isLoading) return;
    reset(defaultValues);
    setValue(
      "holiday-switch",
      !!defaultValues?.["open-Holiday"] && !!defaultValues?.["close-Holiday"],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, defaultValues, isLoading, reset, setValue]);

  useEffect(() => {
    handleSetFormValues();
  }, [handleSetFormValues]);

  useEffect(() => {
    if (!holidayWatcher) {
      setValue("holiday-switch", false);
    }
  }, [holidayWatcher, setValue]);

  const renderHolidayForm = useCallback(() => {
    if (!editMode) {
      return holidayForm?.map((item: any) => (
        <Styled.WorkingHourContainer key={item?.day}>
          <Styled.WorkingHourRow key={item?.day}>
            <Styled.WeekDayTypography
              variant="body2"
              fontWeight="bold"
              isActive={!!switchHolidayWatcher}
            >
              {t("holiday")}
            </Styled.WeekDayTypography>
            {switchHolidayWatcher ? (
              <>
                <Styled.HoursContainerView>
                  <Styled.HourContainer>
                    <Typography mt={-0.5} alignContent="center">
                      {item?.open?.substring(0, 5)}
                    </Typography>
                  </Styled.HourContainer>
                  {item?.open && item?.close && (
                    <Styled.LegendTypography variant="caption">
                      {t("to")}
                    </Styled.LegendTypography>
                  )}
                  <Styled.HourContainer>
                    <Typography mt={-0.5}>
                      {item?.close?.substring(0, 5)}
                    </Typography>
                  </Styled.HourContainer>
                </Styled.HoursContainerView>

                <Styled.ContainerButton isActive={switchHolidayWatcher} enabled>
                  <Styled.CustomSwitch
                    name="holiday-switch"
                    control={control}
                    setValue={setValue}
                    size="small"
                    disabled
                    color="success"
                  />
                  <Typography>
                    {switchHolidayWatcher ? t("opening") : t("closed")}
                  </Typography>
                </Styled.ContainerButton>
              </>
            ) : (
              <Styled.ContainerDisabled>
                <Styled.ContainerIcon>
                  <HistoryToggleOffIcon fontSize="medium" />
                </Styled.ContainerIcon>
                <Styled.ContainerButton isActive={switchHolidayWatcher} enabled>
                  <Styled.CustomSwitch
                    name="holiday-switch"
                    control={control}
                    setValue={setValue}
                    size="small"
                    color="success"
                    disabled={!holidayWatcher}
                  />
                  <Typography>
                    {switchHolidayWatcher ? t("opening") : t("closed")}
                  </Typography>
                </Styled.ContainerButton>
              </Styled.ContainerDisabled>
            )}
          </Styled.WorkingHourRow>
          {getHoliday(item?.day) && (
            <HolidayWarning
              holidayName={getHoliday(item?.day)?.description as string}
            />
          )}
        </Styled.WorkingHourContainer>
      ));
    }

    return holidayForm?.map((item: any) => (
      <Styled.WorkingHourContainer key={item?.day}>
        <Styled.WorkingHourRow>
          <Styled.WeekDayTypography
            variant="body2"
            fontWeight="bold"
            isActive={!!holidayWatcher}
          >
            {t("holiday")}
          </Styled.WeekDayTypography>
          <Styled.HoursContainer>
            {holidayWatcher && switchHolidayWatcher ? (
              <Styled.ContainerInputHours>
                <Box>
                  <TimePicker
                    disableOpenPicker
                    control={control}
                    defaultValue={item?.open}
                    name="open-Holiday"
                    label={t("open")}
                    setValue={setValue}
                    disabled={!switchHolidayWatcher}
                    rules={{
                      minLength: 5,
                      ...timeValidationRule,
                      required: true,
                    }}
                  />
                </Box>
                <Styled.Separator>{t("to")}</Styled.Separator>
                <Box>
                  <TimePicker
                    disableOpenPicker
                    control={control}
                    defaultValue={item?.close}
                    name="close-Holiday"
                    label={t("close")}
                    setValue={setValue}
                    disabled={!switchHolidayWatcher}
                    rules={{
                      minLength: 5,
                      ...timeValidationRule,
                      ...timeRangeValidationRule(item?.day),
                      required: true,
                    }}
                  />
                </Box>

                <Styled.ContainerButton isActive={false}>
                  <Styled.CustomSwitch
                    name="holiday-switch"
                    control={control}
                    setValue={setValue}
                    size="small"
                    color="success"
                    disabled={!holidayWatcher}
                  />
                  <Typography>
                    {switchHolidayWatcher ? t("opening") : t("closed")}
                  </Typography>
                </Styled.ContainerButton>
              </Styled.ContainerInputHours>
            ) : (
              <Styled.ContainerDisabled>
                <Styled.ContainerIcon>
                  <HistoryToggleOffIcon fontSize="medium" />
                </Styled.ContainerIcon>
                <Styled.ContainerButton isActive={!!switchHolidayWatcher}>
                  <Styled.CustomSwitch
                    name="holiday-switch"
                    control={control}
                    setValue={setValue}
                    size="small"
                    color="success"
                    disabled={!holidayWatcher}
                  />
                  <Typography>
                    {switchHolidayWatcher ? t("opening") : t("closed")}
                  </Typography>
                </Styled.ContainerButton>
              </Styled.ContainerDisabled>
            )}
          </Styled.HoursContainer>
        </Styled.WorkingHourRow>
      </Styled.WorkingHourContainer>
    ));
  }, [
    editMode,
    holidayForm,
    switchHolidayWatcher,
    t,
    control,
    setValue,
    holidayWatcher,
    getHoliday,
    timeValidationRule,
    timeRangeValidationRule,
  ]);

  return (
    <Styled.Container>
      <Accordion expanded={expand}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          onClick={() => setExpand(!expand)}
        >
          <Styled.Title variant="caption">{t("workingHour")}</Styled.Title>
        </AccordionSummary>
        <Styled.Content>
          <Styled.FormContainer>
            {editMode
              ? workingHourData?.map((item) => (
                  <Fragment key={item?.day}>
                    <Styled.WorkingHourContainer>
                      <Styled.WorkingHourRow>
                        <Styled.WeekDayTypography
                          variant="body2"
                          fontWeight="bold"
                          isActive={!!watch()[`enabled-${item?.day}`]}
                        >
                          {t(item?.day?.toLowerCase() || "")}
                        </Styled.WeekDayTypography>
                        <Styled.HoursContainer>
                          {watch()[`enabled-${item?.day}`] ? (
                            <Styled.ContainerInputHours>
                              <Box>
                                <TimePicker
                                  disableOpenPicker
                                  control={control}
                                  defaultValue={item?.open}
                                  name={`open-${item?.day}`}
                                  label={t("open")}
                                  setValue={setValue}
                                  disabled={!watch()[`enabled-${item?.day}`]}
                                  rules={{
                                    minLength: 5,
                                    ...timeValidationRule,
                                    required: true,
                                  }}
                                />
                              </Box>
                              <Styled.Separator>{t("to")}</Styled.Separator>
                              <Box>
                                <TimePicker
                                  disableOpenPicker
                                  control={control}
                                  defaultValue={item?.close}
                                  name={`close-${item?.day}`}
                                  label={t("close")}
                                  setValue={setValue}
                                  disabled={!watch()[`enabled-${item?.day}`]}
                                  rules={{
                                    minLength: 5,
                                    ...timeValidationRule,
                                    ...timeRangeValidationRule(item?.day),
                                    required: true,
                                  }}
                                />
                              </Box>

                              <Styled.ContainerButton isActive={item?.enabled}>
                                <Styled.CustomSwitch
                                  name={`enabled-${item?.day}`}
                                  control={control}
                                  setValue={setValue}
                                  size="small"
                                  color="success"
                                />
                                <Typography>
                                  {watch()[`enabled-${item?.day}`]
                                    ? t("opening")
                                    : t("closed")}
                                </Typography>
                              </Styled.ContainerButton>
                            </Styled.ContainerInputHours>
                          ) : (
                            <Styled.ContainerDisabled>
                              <Styled.ContainerIcon>
                                <HistoryToggleOffIcon fontSize="medium" />
                              </Styled.ContainerIcon>
                              <Styled.ContainerButton
                                isActive={!!watch()[`enabled-${item?.day}`]}
                              >
                                <Styled.CustomSwitch
                                  name={`enabled-${item?.day}`}
                                  control={control}
                                  setValue={setValue}
                                  size="small"
                                  color="success"
                                />
                                <Typography>
                                  {watch()[`enabled-${item?.day}`]
                                    ? t("opening")
                                    : t("closed")}
                                </Typography>
                              </Styled.ContainerButton>
                            </Styled.ContainerDisabled>
                          )}
                        </Styled.HoursContainer>
                      </Styled.WorkingHourRow>
                      {getHoliday(item?.day) && (
                        <HolidayWarning
                          holidayName={
                            getHoliday(item?.day)?.description as string
                          }
                        />
                      )}
                    </Styled.WorkingHourContainer>
                  </Fragment>
                ))
              : workingHourData?.map((item) => (
                  <Styled.WorkingHourContainer key={item?.day}>
                    <Styled.WorkingHourRow key={item?.day}>
                      <Styled.WeekDayTypography
                        variant="body2"
                        fontWeight="bold"
                        isActive={item?.enabled}
                      >
                        {t(item?.day?.toLowerCase())}
                      </Styled.WeekDayTypography>
                      {item?.enabled ? (
                        <>
                          <Styled.HoursContainerView>
                            <Styled.HourContainer>
                              <Typography mt={-0.5} alignContent="center">
                                {item?.open?.substring(0, 5)}
                              </Typography>
                            </Styled.HourContainer>
                            {item?.open && item?.close && (
                              <Styled.LegendTypography variant="caption">
                                {t("to")}
                              </Styled.LegendTypography>
                            )}
                            <Styled.HourContainer>
                              <Typography mt={-0.5}>
                                {item?.close?.substring(0, 5)}
                              </Typography>
                            </Styled.HourContainer>
                          </Styled.HoursContainerView>

                          <Styled.ContainerButton
                            isActive={item?.enabled}
                            enabled
                          >
                            <Styled.CustomSwitch
                              name={`enabled-${item?.day}`}
                              control={control}
                              setValue={setValue}
                              size="small"
                              disabled
                              color="success"
                            />
                            <Typography>
                              {item?.enabled ? t("opening") : t("closed")}
                            </Typography>
                          </Styled.ContainerButton>
                        </>
                      ) : (
                        <Styled.ContainerDisabled>
                          <Styled.ContainerIcon>
                            <HistoryToggleOffIcon fontSize="medium" />
                          </Styled.ContainerIcon>
                          <Styled.ContainerButton
                            isActive={item?.enabled}
                            enabled
                          >
                            <Styled.CustomSwitch
                              name={`enabled-${item?.day}`}
                              control={control}
                              disabled
                              setValue={setValue}
                              size="small"
                              color="success"
                            />
                            <Typography>
                              {item?.enabled ? t("opening") : t("closed")}
                            </Typography>
                          </Styled.ContainerButton>
                        </Styled.ContainerDisabled>
                      )}
                    </Styled.WorkingHourRow>
                    {getHoliday(item?.day) && (
                      <HolidayWarning
                        holidayName={
                          getHoliday(item?.day)?.description as string
                        }
                      />
                    )}
                  </Styled.WorkingHourContainer>
                ))}
          </Styled.FormContainer>
          {editMode && (
            <Box mt={2} ml={-1} display="flex" alignItems="center">
              <Controller
                name="enabled-Holiday"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value as boolean}
                    onChange={(e) => field.onChange(e.target.checked)}
                    id="holiday"
                  />
                )}
              />
              <Typography component="label" fontSize={14} htmlFor="holiday">
                Estabelecer horário padrão para feriados
              </Typography>
            </Box>
          )}

          {/* FERIADO */}
          {/* FERIADO */}
          {/* FERIADO */}
          {/* FERIADO */}
          <Styled.FormContainer
            style={{ marginTop: editMode ? "0px" : "16px" }}
          >
            {renderHolidayForm()}
          </Styled.FormContainer>
          {!!Object.keys(errors).length && (
            <Typography color="primary" ml={1} mt={1} variant="caption">
              {t("hoursAreRequired")}
            </Typography>
          )}
          <Styled.ActionsContainer>
            {editMode ? (
              <>
                <Button
                  onClick={handleChangeEditMode}
                  color="inherit"
                  variant="outlined"
                  endIcon={<CloseIcon />}
                >
                  {t("close")}
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  endIcon={<SaveIcon />}
                  onClick={handleSubmit((formData) =>
                    submitWorkingHourUpdate(formData),
                  )}
                >
                  {t("apply")}
                </Button>
              </>
            ) : (
              <Button
                disableElevation
                disableRipple
                onClick={handleChangeEditMode}
                color="error"
                variant="contained"
                endIcon={<EditIcon />}
              >
                {t("editOperation")}
              </Button>
            )}
          </Styled.ActionsContainer>
        </Styled.Content>
      </Accordion>
    </Styled.Container>
  );
};

export default WorkingHour;
